/**
 * Buddy requests the member sent in this tab.
 *
 * GetOtherUserProfile reports `isBuddy` and `invited` (an invite waiting on the
 * viewer) but nothing for a request the viewer already sent, so a profile opened
 * after requesting someone from the Buddies page would offer "Request Buddy"
 * again. This is a UI hint only: the server's `isBuddy` always wins, and the
 * record is dropped once the request is accepted.
 */
const STORAGE_KEY = '@wlb/sent-buddy-requests';

const read = (): number[] => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === 'number') : [];
  } catch {
    return [];
  }
};

const write = (ids: number[]): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Private-mode or quota failures only cost the hint, never the request.
  }
};

const toId = (userId?: number | string | null): number | null => {
  const id = Number(userId);
  return Number.isFinite(id) && id > 0 ? id : null;
};

export type BuddyRequestButtonState = 'REQUEST' | 'REQUESTED' | 'ACCEPTED';

interface BuddyRequestStateSource {
  userId?: number | string | null;
  status?: string | null;
  isRequested?: boolean;
  invited?: boolean;
  isAccepted?: boolean;
}

export const rememberBuddyRequest = (userId?: number | string | null): void => {
  const id = toId(userId);
  if (id === null) return;
  const ids = read();
  if (!ids.includes(id)) write([...ids, id]);
};

export const forgetBuddyRequest = (userId?: number | string | null): void => {
  const id = toId(userId);
  if (id === null) return;
  const ids = read();
  if (ids.includes(id)) write(ids.filter(existing => existing !== id));
};

export const hasPendingBuddyRequest = (userId?: number | string | null): boolean => {
  const id = toId(userId);
  return id !== null && read().includes(id);
};

/**
 * Resolves a buddy-search button from both the server row and requests sent
 * elsewhere in this tab. Explicit terminal server states take precedence over
 * the local hint, which may outlive an accepted or declined request.
 */
export const getBuddyRequestButtonState = (
  user: BuddyRequestStateSource,
): BuddyRequestButtonState => {
  const status = user.status?.trim().toLowerCase() ?? '';

  if (status === 'accepted' || user.isAccepted === true) return 'ACCEPTED';
  if (status === 'declined') return 'REQUEST';
  if (
    hasPendingBuddyRequest(user.userId) ||
    (status === 'pending' && user.isRequested === true) ||
    user.isRequested === true ||
    user.invited === true
  ) {
    return 'REQUESTED';
  }
  return 'REQUEST';
};
